"use client"

import { useState, useEffect } from "react"
import { useAuth } from '../contexts/AuthContext'
import { documentService } from '../services/documentService'
import { apiService } from '../services/api';

// Importar componentes
import Sidebar from '../components/admin/Sidebar'
import Header from '../components/admin/Header'
import StatsCards from '../components/admin/StatsCards'
import UploadForm from '../components/admin/UploadForm'
import DocumentList from '../components/admin/DocumentList'
import AuditTrail from '../components/AuditTrail' // 👈 1. IMPORTAR EL COMPONENTE DE AUDIT TRAIL

import AboutPage from '../components/admin/pages/AboutPage';
import TermsPage from '../components/admin/pages/TermsPage';
import PrivacyPage from '../components/admin/pages/PrivacyPage';
import TemplateManager from '../components/admin/TemplateManager' 
import ClientManager from '../components/admin/ClientManager' 

const API_URL_server = apiService.baseURL;

export default function AdminPanel() {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  
  const [generatedSigners, setGeneratedSigners] = useState([])
  
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [activeSection, setActiveSection] = useState("inicio")
  const [regeneratingLinks, setRegeneratingLinks] = useState({})
  const [viewingLinks, setViewingLinks] = useState({})
  const [uploadError, setUploadError] = useState("")
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  // 👇 2. ESTADOS YA EXISTENTES PARA EL AUDIT TRAIL
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);

  const { user, logout } = useAuth()

  useEffect(() => {
    loadDocuments()
  }, [])

  const loadDocuments = async () => {
    try {
      const data = await documentService.getDocuments()
      if (data.success) setDocuments(data.documents)
    } catch (error) {
      if (error.message.includes('Sesión expirada')) await logout()
    }
  }

  const handleUpload = async (formData) => {
    setLoading(true);
    setUploadError("");

    try {
      const data = await documentService.uploadDocument(formData);
      if (data.success) {
        setGeneratedSigners(data.signers || []);
        await loadDocuments();
      } else {
        setUploadError(data.error || "Error desconocido al procesar el documento");
      }
    } catch (error) {
      setUploadError(error.message || "Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  }

  const clearUploadForm = () => {
    setGeneratedSigners([]);
    setUploadError("");
  }

  // 👇 3. HANDLER YA EXISTENTE PARA ABRIR EL MODAL
  const handleViewAuditTrail = (docId) => {
    setSelectedDocumentId(docId);
    setShowAuditTrail(true);
  };

  const viewLink = async (documentId) => {
    if (viewingLinks[documentId]) {
      hideLink(documentId);
      return;
    }

    try {
      const response = await documentService.getDocumentDetails(documentId);
      
      if (response && response.success) {
        setViewingLinks((prev) => ({
          ...prev,
          [documentId]: {
            link: apiService.getSignatureUrl(documentId),
            signers: response.signers || [],
            document: response.document,
          },
        }));
      } else {
        throw new Error(response?.error || "Error al obtener detalles");
      }
    } catch (error) {
      console.error("Error al obtener los links de firma:", error);
      alert("Error al cargar los links. Inténtalo de nuevo.");
    }
  };

  const hideLink = (documentId) => {
    setViewingLinks((prev) => {
      const newLinks = { ...prev }
      delete newLinks[documentId]
      return newLinks
    })
  }

  const regenerateLink = async (documentId) => {
    setRegeneratingLinks((prev) => ({ ...prev, [documentId]: true }))
    try {
      const data = await documentService.regenerateLink(documentId)
      if (data.success) {
        alert("Links regenerados exitosamente")
        await viewLink(documentId);
        await loadDocuments()
      } else {
        alert("Error al regenerar el link: " + (data.error || "Error desconocido"))
      }
    } catch (error) {
      alert(error.message || "Error al conectar con el servidor")
    } finally {
      setRegeneratingLinks((prev) => ({ ...prev, [documentId]: false }))
    }
  }

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link)
    alert("✓ Link copiado al portapapeles")
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleString("es-ES", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    })
  }

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadDocuments();
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Error al refrescar:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return "N/A"
    const now = new Date()
    const expires = new Date(expiresAt)
    const diff = expires - now
    if (diff <= 0) return "Expirado"
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    return days > 0 ? `${days}d ${hours}h` : `${hours}h`
  }

  const downloadOriginalDocument = async (documentId, originalName) => {
    try {
      await apiService.downloadFile(
        `/documents/${documentId}/download/original`, 
        originalName || 'documento.pdf'
      );
    } catch (error) {
      alert(error.message || "Error al descargar el documento");
    }
  }

  const downloadSignedDocument = async (documentId, originalName) => {
    try {
      await apiService.downloadFile(
        `/documents/${documentId}/download/signed`, 
        `firmado-${originalName || 'documento'}.pdf`
      );
    } catch (error) {
      alert(error.message || "Error al descargar el documento firmado");
    }
  }

  const sendLinksToEmail = () => {
    const emails = generatedSigners.filter(s => s.email).map(s => s.email).join(', ');
    if (!emails) {
      alert("No hay correos electrónicos registrados para los firmantes.");
      return;
    }
    alert(`📧 Simulación: Links enviados a: ${emails}\n(Aquí se conectaría con tu servicio de envío de correos)`);
  }

  const handleLogout = async () => {
    await logout()
  }

  const stats = {
    total: documents.length,
    signed: documents.filter((doc) => doc.status === "signed").length,
    pending: documents.filter((doc) => doc.status === "pending").length,
    expired: documents.filter((doc) => doc.status === "expired").length,
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'inicio':
        return (
          <>
            <StatsCards stats={stats} />
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-8">
              <div className="p-6 lg:p-8">
                <UploadForm
                  generatedSigners={generatedSigners}
                  uploadError={uploadError}
                  loading={loading}
                  onUpload={handleUpload}
                  onClear={clearUploadForm}
                  onCopyLink={copyToClipboard}
                  onSendEmail={sendLinksToEmail}
                />
              </div>
            </div>
          </>
        );
      
      case 'documents':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 lg:p-8">
              <DocumentList
                documents={documents}
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                viewingLinks={viewingLinks}
                regeneratingLinks={regeneratingLinks}
                loading={isRefreshing}
                onSearchChange={setSearchTerm}
                onFilterChange={setStatusFilter}
                onViewLink={viewLink}
                onHideLink={hideLink}
                onCopyLink={copyToClipboard}
                onRegenerateLink={regenerateLink}
                onDownloadOriginal={downloadOriginalDocument}
                onDownloadSigned={downloadSignedDocument}
                onVerify={(id) => window.open(apiService.getVerificationUrl(id), '_blank')}
                onRefresh={handleRefresh}
                onViewAuditTrail={handleViewAuditTrail} // 👈 4. PASAR LA FUNCIÓN AL COMPONENTE DE LISTA
                formatDateTime={formatDateTime}
                getTimeRemaining={getTimeRemaining}
              />
            </div>
          </div>
        );

      case 'templates':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 lg:p-8">
              <TemplateManager />
            </div>
          </div>
        );
        
      case 'clientes':
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 lg:p-8">
              <ClientManager />
            </div>
          </div>
        );

      case 'about':
        return <AboutPage />;
      
      case 'terms':
        return <TermsPage />;
      
      case 'policies':
        return <PrivacyPage />;

      default:
        return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Sección en desarrollo</h3>
            <p className="text-slate-500">La sección "{activeSection}" estará disponible pronto.</p>
          </div>
        );
    }
  };

  return (
    <div className="h-screen bg-slate-50 font-sans flex overflow-hidden">
      <Sidebar 
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      <div className="flex-1 flex flex-col min-w-0 h-full">
        <Header 
          user={user}
          onLogout={handleLogout}
          onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
          activeSection={activeSection}
        />

        <main className="flex-1 overflow-y-auto p-6 lg:p-8 scroll-smooth">
          <div className="max-w-7xl mx-auto pb-8">
            {activeSection === 'documents' && (
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
                  Gestión de Documentos
                </h1>
                <p className="text-slate-500 mt-2 text-base">
                  Busca, filtra y administra todos los documentos del sistema
                </p>
              </div>
            )}

            {renderContent()}
          </div>
        </main>
      </div>

      {/* 👇 5. RENDERIZAR EL MODAL DE AUDIT TRAIL AL FINAL DEL COMPONENTE */}
      {showAuditTrail && selectedDocumentId && (
        <AuditTrail 
          documentId={selectedDocumentId} 
          onClose={() => setShowAuditTrail(false)} 
        />
      )}
    </div>
  )
}